import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Buildings, Crosshair } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function MandiSearch() {
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [pin, setPin] = useState("");
  const [states, setStates] = useState([]);
  const [rows, setRows] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/mandis/states");
        setStates(data);
      } catch {}
    })();
  }, []);

  const load = async () => {
    setRows(null);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (state) params.set("state", state);
      if (district) params.set("district", district);
      if (pin) params.set("pincode", pin);
      const { data } = await api.get(`/mandis?${params.toString()}`);
      setRows(data);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, state, district, pin]);

  const locateNearby = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { data } = await api.get(`/mandis/nearby?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
        setRows(data);
        toast.success(`Found ${data.length} nearby mandis`);
      } catch { toast.error("Nearby search failed"); }
    }, () => toast.error("Location access denied"));
  };

  return (
    <div className="space-y-6" data-testid="mandi-search-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-stone-500">Discovery</div>
          <div className="font-display text-2xl font-bold text-stone-900">Pan-India Mandi Directory</div>
        </div>
        <button onClick={locateNearby} className="inline-flex items-center gap-2 px-4 py-2 rounded-md border border-emerald-800 text-emerald-900 hover:bg-emerald-900 hover:text-white font-bold text-sm transition-colors" data-testid="locate-nearby-btn">
          <Crosshair weight="bold" size={16} /> Locate Nearby
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-xl bg-white border border-stone-200 p-4 grid sm:grid-cols-4 gap-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search mandi name…" className="px-3 py-2 rounded-md border border-stone-300 text-sm" data-testid="mandi-q" />
        <select value={state} onChange={(e) => setState(e.target.value)} className="px-3 py-2 rounded-md border border-stone-300 text-sm" data-testid="mandi-state">
          <option value="">All states</option>
          {states.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={district} onChange={(e) => setDistrict(e.target.value)} placeholder="District" className="px-3 py-2 rounded-md border border-stone-300 text-sm" data-testid="mandi-district" />
        <input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="PIN code" className="px-3 py-2 rounded-md border border-stone-300 text-sm" data-testid="mandi-pin" />
      </div>

      {/* Results */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows === null && Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="rounded-xl bg-white border border-stone-200 h-40 shimmer" />
        ))}
        {rows && rows.length === 0 && <div className="col-span-full rounded-xl bg-white border border-stone-200 p-10 text-center text-stone-500">No mandis match your filters.</div>}
        {rows && rows.map((m, i) => (
          <motion.div key={m.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="lift rounded-xl bg-white border border-stone-200 p-5" data-testid={`mandi-card-${m.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-emerald-100 text-emerald-800 grid place-items-center">
                <Buildings weight="duotone" size={22} />
              </div>
              <div>
                <div className="font-display text-lg font-bold text-stone-900">{m.name}</div>
                <div className="text-xs text-stone-500">{m.district}, {m.state}</div>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between text-sm">
              <div className="flex items-center gap-1 text-stone-600"><MapPin size={14} /> PIN {m.pincode}</div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
